/**
 * SectionStory — how a section was drafted, told in a few short paragraphs;
 * the opening view of the Actions tab.
 *
 * Which documents and sections it drew on, what it searched, which tools
 * worked out figures, what it wrote and what it left for the writer, and what
 * has happened to it since. Each figure it worked out opens into its
 * calculation. shared/client/sectionStory.ts builds every sentence from the
 * record with fixed wording; no model writes them. The web app's side-pane
 * SectionStoryView is its twin.
 */

import { makeStyles } from "@fluentui/react-components";
import { ChevronDown12Regular, ChevronRight12Regular } from "@fluentui/react-icons";
import type { ActionHistoryPayload } from "@shared/client/actionHistory";
import { buildSectionStory } from "@shared/client/sectionStory";
import type React from "react";
import { useMemo, useState } from "react";
import { fonts, inkColors, surfaceColors } from "../../tokens";
import DerivationTrace from "../DerivationTrace";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "12px", padding: 0, fontFamily: fonts.body },
  summary: { display: "flex", flexDirection: "column", gap: "8px" },
  para: { margin: 0, fontSize: "13px", lineHeight: 1.55, color: inkColors.ink, wordBreak: "break-word" },
  empty: { margin: 0, fontSize: "12px", color: inkColors.ink3 },
  block: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingTop: "8px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  title: {
    margin: 0,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "4px" },
  toggle: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    width: "100%",
    padding: "2px 0",
    border: "none",
    borderRadius: "3px",
    background: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.45,
    color: inkColors.ink,
    wordBreak: "break-word",
    ":hover": { background: surfaceColors.paperDim },
    ":focus-visible": { outline: `2px solid ${inkColors.ink}`, outlineOffset: "1px" },
  },
  chev: { flexShrink: 0, marginTop: "3px", color: inkColors.ink4 },
  card: { marginTop: "6px", border: `1px solid ${surfaceColors.line}`, borderRadius: "4px" },
  hint: {
    margin: 0,
    paddingTop: "8px",
    borderTop: `1px solid ${surfaceColors.line}`,
    fontSize: "11px",
    color: inkColors.ink4,
  },
  link: {
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
    textDecoration: "underline",
    ":hover": { color: inkColors.ink },
  },
});

export const SectionStory: React.FC<{ payload: ActionHistoryPayload; onShowSteps?: () => void }> = ({
  payload,
  onShowSteps,
}) => {
  const styles = useStyles();
  const story = useMemo(() => buildSectionStory(payload), [payload]);
  const [open, setOpen] = useState<string | null>(null);
  const calculations = (story.chapters.find((c) => c.key === "worked")?.lines ?? []).filter((l) => l.derivation);
  return (
    <div className={styles.root} data-testid="section-story">
      {story.summary.length === 0 ? (
        <p className={styles.empty}>Nothing has been recorded for this section yet.</p>
      ) : (
        <div className={styles.summary}>
          {story.summary.map((paragraph) => (
            <p key={paragraph} className={styles.para}>
              {paragraph}
            </p>
          ))}
        </div>
      )}
      {calculations.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.title}>How the figures were worked out</h3>
          <ul className={styles.list}>
            {calculations.map((line) => {
              const cardOpen = open === line.key;
              return (
                <li key={line.key}>
                  <button
                    type="button"
                    className={styles.toggle}
                    aria-expanded={cardOpen}
                    onClick={() => setOpen(cardOpen ? null : line.key)}
                  >
                    <span className={styles.chev} aria-hidden="true">
                      {cardOpen ? <ChevronDown12Regular /> : <ChevronRight12Regular />}
                    </span>
                    <span>{line.text}</span>
                  </button>
                  {cardOpen && line.derivation && (
                    <div className={styles.card}>
                      <DerivationTrace derivation={line.derivation} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <p className={styles.hint}>
        Every step, with what each one read and returned, is under{" "}
        {onShowSteps ? (
          <button type="button" className={styles.link} onClick={onShowSteps}>
            All steps
          </button>
        ) : (
          "All steps"
        )}
        .
      </p>
    </div>
  );
};

export default SectionStory;
